// <map-feature-info> — a hover-tooltip rendered as <template> content,
// with [data-prop=X] placeholders filled from feature.get(X).
//
// Place inside (or as a sibling of) a <map-box> that shares an [eventbus]
// ancestor. On `feature:hover`, clones the <template> child and fills it.
// On `feature:unhover`, hides itself.
//
// Example:
//   <map-feature-info>
//     <template>
//       <strong data-prop="species_name"></strong>:
//       <span data-prop="quantity"></span> Stück
//       (<span data-prop="date" data-format="date"></span>)
//     </template>
//   </map-feature-info>
//
// Supported data-format values:
//   date — parses the value as a Date and formats with the document locale
//   datetime — same plus time
//   (default) — feature.get(prop) coerced to string
//
// Without a <template> child, no rendering happens.

const dateFormatter     = new Intl.DateTimeFormat();
const datetimeFormatter = new Intl.DateTimeFormat(undefined, {dateStyle: 'medium', timeStyle: 'short'});


function formatValue(raw, format) {
  if (raw == null) return '';
  switch (format) {
    case 'date':
      try { return dateFormatter.format(new Date(raw)); }
      catch (_) { return String(raw); }
    case 'datetime':
      try { return datetimeFormatter.format(new Date(raw)); }
      catch (_) { return String(raw); }
    default:
      return String(raw);
  }
}


export class MapFeatureInfo extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    this.eventBus = this.closest('[eventbus]') || this.closest('map-box') || this.parentElement;
    if (!this.eventBus) return;

    this._onHover   = this.renderFeature.bind(this);
    this._onUnhover = this.hide.bind(this);
    this.eventBus.addEventListener('feature:hover',   this._onHover);
    this.eventBus.addEventListener('feature:unhover', this._onUnhover);

    this.template = this.querySelector(':scope > template');

    this.contentHost = document.createElement('div');
    this.contentHost.className = 'map-feature-info-content';
    this.appendChild(this.contentHost);

    this.hide();
  }

  disconnectedCallback() {
    if (this.eventBus) {
      this.eventBus.removeEventListener('feature:hover',   this._onHover);
      this.eventBus.removeEventListener('feature:unhover', this._onUnhover);
    }
    this.eventBus = undefined;
  }

  renderFeature(event) {
    if (!this.template) return;
    const feature = event.detail.feature;

    const fragment = this.template.content.cloneNode(true);
    fragment.querySelectorAll('[data-prop]').forEach(el => {
      const prop   = el.dataset.prop;
      const format = el.dataset.format;
      el.textContent = formatValue(feature.get(prop), format);
    });

    this.contentHost.replaceChildren(fragment);
    this.dataset.visible = 'true';
  }

  hide() {
    if (this.contentHost) this.contentHost.replaceChildren();
    delete this.dataset.visible;
  }
}
